import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoicingApiActionSegmentEvents } from '@features/invoices/hooks/useInvoicingApiActionSegmentEvents'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useCreateInvoiceDraft } from './useCreateInvoiceDraft'
import { useCreateInvoiceNumberingSequence } from './useCreateInvoiceNumberingSequence'
import { useInvoiceDraftPayload } from './useInvoiceDraftPayload'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'
import { useUpdateInvoiceDraft } from './useUpdateInvoiceDraft'

interface SaveDraftOptions {
  formData: InvoiceForm
  onError?: () => void
  onSuccess?: (saveDocumentId: number) => void
}

export const useSaveInvoiceDraft = () => {
  const { t } = useTranslation()
  const { sendEvent } = useInvoicingApiActionSegmentEvents()
  const { getInvoiceDraftPayload, isLoading } = useInvoiceDraftPayload()
  const { createInvoiceDraft, isProcessing: isProcessingCreateInvoiceDraft } = useCreateInvoiceDraft()
  const { createInvoiceNumberingSequence, isProcessing: isProcessingCreateNumberingSequence } =
    useCreateInvoiceNumberingSequence()
  const { update: updateInvoiceDraft, isProcessing: isProcessingUpdateInvoiceDraft } = useUpdateInvoiceDraft()
  const { documentType } = useInvoiceEditorDocumentType()

  const isProcessing =
    isProcessingCreateInvoiceDraft || isProcessingUpdateInvoiceDraft || isProcessingCreateNumberingSequence

  const saveInvoiceDraft = useCallback(
    ({ formData, onError, onSuccess }: SaveDraftOptions) => {
      const invoiceDraftPayload = getInvoiceDraftPayload(documentType, formData)
      const isSavingNewDraft = invoiceDraftPayload?.id === undefined // if we have the id, we're editing an existing draft
      const saveDraftFn = isSavingNewDraft ? createInvoiceDraft : updateInvoiceDraft

      createInvoiceNumberingSequence({
        invoiceNumberPrefix: formData.invoiceNumberPrefix,
        invoiceNumber: formData.invoiceNumber,
      })

      saveDraftFn(
        { payload: invoiceDraftPayload, documentType },
        {
          onError: () => {
            notify({
              id: NotificationKeys.ExternalInvoiceSavedAsDraft,
              message: t('error_saving'),
              variant: 'error',
            })
            onError?.()
          },
          onSuccess: (draftSaveResponse) => {
            const draftId = draftSaveResponse?.data?.id

            queryClient.invalidateQueries([QueryKeys.InvoicesZervant])
            queryClient.invalidateQueries([QueryKeys.InvoicesSummary])
            queryClient.invalidateQueries([QueryKeys.ExternalInvoiceNumberingSequences])
            queryClient.invalidateQueries([QueryKeys.Quotes])
            queryClient.invalidateQueries([QueryKeys.QuotesSummary])
            queryClient.invalidateQueries([QueryKeys.SalesDocumentDraft, draftId, documentType])

            sendEvent.document.draftCreated(documentType)

            onSuccess?.(draftId)
          },
        },
      )
    },
    [
      createInvoiceDraft,
      createInvoiceNumberingSequence,
      documentType,
      getInvoiceDraftPayload,
      sendEvent.document,
      t,
      updateInvoiceDraft,
    ],
  )

  return { saveInvoiceDraft, isLoading, isProcessing }
}
